import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useNavigate, useSearchParams } from "react-router-dom";

import "./themeupdate.css";

import { fetchThemeData } from "../../themeSlice";
import {
  selectAdminToken,
  selectOrganizationId,
  selectSessionId,
} from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { uploadThemeMedia } from "../../functions/uploadThemeMedia";
import { clampQuizFields, validateQuizList } from "./quizHelpers";
import QuizTab from "./QuizTab";
import ThemeAssetFields from "./ThemeAssetFields";
import BoardColorPanel from "./BoardColorPanel";
import ThemeUpdateFeatureTour, {
  markThemeUpdateTourSeen,
  shouldAutoStartThemeUpdateTour,
} from "./ThemeUpdateFeatureTour";
import {
  AVATAR_FIELDS,
  LADDER_FIELDS,
  SNAKE_FIELDS,
  isDefaultAvatarValue,
  toColorInputValue,
  validatePngAsset,
} from "./boardAssetConfig";
import {
  DEFAULT_BOARD_COLS,
  DEFAULT_BOARD_ROWS,
  resolveBoardDims,
} from "../../../functions/boardLayout";

const PAGE_TABS = [
  { id: "avatars", label: "Add Avatar Image" },
  // Board snake/ladder PNGs are drawn in code for now — hide this tab.
  // { id: "board_image", label: "Add Board Image" },
  { id: "board_color", label: "Board Size & Colour" },
  { id: "questions", label: "Add Questions" },
];

const DEFAULT_TAB = PAGE_TABS[0].id;
const TAB_PARAM = "tab";

const DEFAULT_COLOR_1 = "#2bb673";
const DEFAULT_COLOR_2 = "#3171ad";
const DEFAULT_COLOR_3 = "#ffffff";

function tabFromSearch(params) {
  const tab = params.get(TAB_PARAM);
  return PAGE_TABS.some((t) => t.id === tab) ? tab : DEFAULT_TAB;
}

let pairIdCounter = 0;
function newPairId(prefix = "pair") {
  pairIdCounter += 1;
  return `${prefix}-${pairIdCounter}`;
}

function normalizeQuizFromTheme(raw) {
  const list = Array.isArray(raw) ? raw : [];
  return list.map((item) => ({
    id: newPairId("quiz"),
    ...clampQuizFields(item),
  }));
}

const ThemeUpdate = () => {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const activeTab = tabFromSearch(searchParams);
  const [isSaving, setIsSaving] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizErrorMessage, setQuizErrorMessage] = useState("");
  const [quizSuccessMessage, setQuizSuccessMessage] = useState("");
  const [pendingFiles, setPendingFiles] = useState({});
  const [color1, setColor1] = useState(DEFAULT_COLOR_1);
  const [color2, setColor2] = useState(DEFAULT_COLOR_2);
  const [color3, setColor3] = useState(DEFAULT_COLOR_3);
  const [boardCols, setBoardCols] = useState(DEFAULT_BOARD_COLS);
  const [boardRows, setBoardRows] = useState(DEFAULT_BOARD_ROWS);
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setQuizQuestions(normalizeQuizFromTheme(data.quiz));
    setColor1(toColorInputValue(data.set1_color1, DEFAULT_COLOR_1));
    setColor2(toColorInputValue(data.set1_color2, DEFAULT_COLOR_2));
    setColor3(toColorInputValue(data.set1_color3, DEFAULT_COLOR_3));
    const dims = resolveBoardDims(data);
    setBoardCols(dims.cols);
    setBoardRows(dims.rows);
  }, [data]);

  useEffect(() => {
    if (!data) return;
    if (!shouldAutoStartThemeUpdateTour()) return;
    const t = window.setTimeout(() => setTourOpen(true), 400);
    return () => window.clearTimeout(t);
  }, [data]);

  const closeTour = useCallback(() => {
    markThemeUpdateTourSeen();
    setTourOpen(false);
  }, []);

  const changeTab = useCallback(
    (tabId) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tabId === DEFAULT_TAB) next.delete(TAB_PARAM);
          else next.set(TAB_PARAM, tabId);
          return next;
        },
        { replace: true }
      );
      setQuizErrorMessage("");
      setQuizSuccessMessage("");
    },
    [setSearchParams]
  );

  const handleTourStepChange = useCallback(
    (step) => {
      if (!step?.tab) return;
      changeTab(step.tab);
    },
    [changeTab]
  );

  const pickFile = (key, file) => {
    setPendingFiles((prev) => {
      const next = { ...prev };
      if (!file) delete next[key];
      else next[key] = file;
      return next;
    });
  };

  const uploadFields = async (fields) => {
    const selected = fields.filter((field) => pendingFiles[field.key]);
    if (!selected.length) {
      Swal.fire("Error", "Please select at least one image to upload.", "error");
      return;
    }

    setIsSaving(true);
    try {
      for (const field of selected) {
        const error = await validatePngAsset(pendingFiles[field.key], field);
        if (error) {
          Swal.fire("Error", error, "error");
          return;
        }
      }

      const formData = new FormData();
      formData.append("themeName", currentTheme);
      selected.forEach((field) => {
        formData.append(field.key, pendingFiles[field.key]);
      });

      await uploadThemeMedia({ formData, token: adminToken });
      setPendingFiles((prev) => {
        const next = { ...prev };
        selected.forEach((field) => delete next[field.key]);
        return next;
      });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire("Success", "Data Updated", "success");
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to upload images.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAvatar = async (field) => {
    const stored = data?.[field.key];
    if (isDefaultAvatarValue(stored, field)) {
      Swal.fire("Error", "Default avatars cannot be deleted.", "error");
      return;
    }

    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This will reset the avatar to the default image.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, reset it",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    setIsSaving(true);
    try {
      await updateThemeData({
        payload: {
          data: { [field.key]: field.defaultFile },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire("Success", "Successfully Deleted", "success");
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to reset avatar.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveBoard = async () => {
    setIsSaving(true);
    try {
      const cols = boardCols;
      const rows = boardRows;
      await updateThemeData({
        payload: {
          data: {
            set1_color1: color1,
            set1_color2: color2,
            set1_color3: color3,
            board_cols: cols,
            board_rows: rows,
            board_size: cols * rows,
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire("Success", "Data Updated", "success");
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save board settings.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const persistQuizQuestions = async (nextQuestions) => {
    const validation = validateQuizList(nextQuestions.map(({ id, ...rest }) => rest));
    if (!validation.ok) {
      throw new Error(validation.message);
    }

    setIsSaving(true);
    setQuizErrorMessage("");
    setQuizSuccessMessage("");

    try {
      await updateThemeData({
        payload: {
          data: {
            quiz: validation.data,
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      setQuizQuestions(
        nextQuestions.map((q, i) => ({
          id: q.id || newPairId("quiz"),
          ...validation.data[i],
        }))
      );
    } catch (err) {
      setQuizErrorMessage(err?.message || "Failed to save questions to theme.");
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const handleColorChange = (key, value) => {
    if (key === "color1") setColor1(value);
    else if (key === "color2") setColor2(value);
    else setColor3(value);
  };

  const avatarGroups = useMemo(
    () => [{ title: "Add Avatar Image", fields: AVATAR_FIELDS }],
    []
  );
  const boardImageGroups = useMemo(
    () => [
      { title: "Add Snakes Image", fields: SNAKE_FIELDS },
      { title: "Add ladder Image", fields: LADDER_FIELDS },
    ],
    []
  );

  if (status === "loading" && !data) {
    return (
      <div className="themeupdate-page">
        <div className="themeupdate-loading">Loading theme data…</div>
      </div>
    );
  }

  return (
    <div className="themeupdate-page">
      <div className="back-button-holder admin-tour-top-bar">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
        <button
          type="button"
          className="admin-tour-launch"
          onClick={() => setTourOpen(true)}
          aria-label="Start feature tour"
        >
          <i className="fa-solid fa-route" aria-hidden />
          Take a tour
        </button>
      </div>

      <header className="themeupdate-header themeupdate-panel-wide" data-tour="tour-gc-header">
        <h1 className="themeupdate-title">Game Content</h1>
        <p className="themeupdate-muted">
          Upload avatars, snakes, ladders, board colours, and questions for this Snakes and Ladders
          theme. Questions are stored on the theme, not in a separate questions table.
        </p>
      </header>

      <nav className="themeupdate-tabs themeupdate-tabs-end" aria-label="Game content tabs" data-tour="tour-gc-tabs">
        {PAGE_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`themeupdate-tab themeupdate-tab-outline${
              activeTab === tab.id ? " active" : ""
            }`}
            onClick={() => changeTab(tab.id)}
          >
            <i className="fa-solid fa-plus-square" aria-hidden /> {tab.label}
          </button>
        ))}
      </nav>

      {activeTab === "avatars" ? (
        <ThemeAssetFields
          groups={avatarGroups}
          themeData={data}
          pendingFiles={pendingFiles}
          onPickFile={pickFile}
          onUpload={() => uploadFields(AVATAR_FIELDS)}
          onDelete={handleDeleteAvatar}
          isSaving={isSaving}
          tourId="tour-gc-avatars"
        />
      ) : null}

      {activeTab === "board_image" ? (
        <ThemeAssetFields
          groups={boardImageGroups}
          themeData={data}
          pendingFiles={pendingFiles}
          onPickFile={pickFile}
          onUpload={() => uploadFields([...SNAKE_FIELDS, ...LADDER_FIELDS])}
          isSaving={isSaving}
          tourId="tour-gc-board-images"
        />
      ) : null}

      {activeTab === "board_color" ? (
        <BoardColorPanel
          color1={color1}
          color2={color2}
          color3={color3}
          boardCols={boardCols}
          boardRows={boardRows}
          onChangeColor={handleColorChange}
          onChangeBoardCols={setBoardCols}
          onChangeBoardRows={setBoardRows}
          onSave={handleSaveBoard}
          isSaving={isSaving}
        />
      ) : null}

      {activeTab === "questions" ? (
        <QuizTab
          questions={quizQuestions}
          setQuestions={setQuizQuestions}
          isSaving={isSaving}
          onPersist={persistQuizQuestions}
          errorMessage={quizErrorMessage}
          successMessage={quizSuccessMessage}
          setErrorMessage={setQuizErrorMessage}
          setSuccessMessage={setQuizSuccessMessage}
          newQuestionId={() => newPairId("quiz")}
          multiLanguage={String(data?.MultiLanguage ?? "true").toLowerCase() === "true"}
        />
      ) : null}

      <ThemeUpdateFeatureTour
        open={tourOpen}
        onClose={closeTour}
        onStepChange={handleTourStepChange}
      />
    </div>
  );
};

export default ThemeUpdate;
